import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MCPConfig } from '@shared/index';
import { Tool } from 'openai/resources/responses/responses';
import { firstValueFrom } from 'rxjs';
import { CounterService } from './counter.service';

export type CreateMCPConfig = {
  type: "managed";
  enabled: boolean;
  toolName: "miro";
  description?: string;
  allowed_tools: string[];
  require_approval?: Tool.Mcp["require_approval"];
} | {
  type: "managed";
  enabled: boolean;
  toolName: "reflactTrain";
  allowed_tools: string[];
  require_approval?: Tool.Mcp["require_approval"];
  serverLabel: string;
  serverUrl: string;
  requiresAuth: boolean;
  accountKey?: string;
} | {
  type: "external";
  enabled: boolean;
  tool: {
    server_label: string;
    type: "mcp";
    allowed_tools: string[];
    server_description: string;
    server_url: string;
    authorization?: string;
    headers?: Tool.Mcp["headers"];
    require_approval?: Tool.Mcp["require_approval"];
  };
}

export type UpdateMCPConfig = {
  enabled: boolean;
  toolName: "miro";
  allowed_tools: string[];
  require_approval?: Tool.Mcp["require_approval"];
} | {
  enabled: boolean;
  toolName: "reflactTrain";
  allowed_tools: string[];
  require_approval?: Tool.Mcp["require_approval"];
  serverLabel: string;
  serverUrl: string;
  requiresAuth: boolean;
  accountKey?: string;
} | {
  enabled: boolean;
  tool: {
    server_label: string;
    type: "mcp";
    allowed_tools: string[];
    server_description: string;
    server_url: string;
    authorization?: string;
    headers?: Tool.Mcp["headers"];
    require_approval?: Tool.Mcp["require_approval"];
  };
}

export type MCPTool = { name: string; title: string; description: string; };
export type ManagedMCPServer = { label: string, name: string, serverUrl: string };

@Injectable({
  providedIn: 'root'
})
export class McpService {

  public mcps: MCPConfig[] = [];
  public managedServers: ManagedMCPServer[] = [];

  private http = inject(HttpClient);
  private counter = inject(CounterService);

  public async getMcps(botId: string) {
    try {
      const result = await firstValueFrom(this.http.get<MCPConfig[]>(`/api/toolbox/bot/admin/mcp/get/${botId}`));
      this.mcps = result;
      this.counter.setMcps(botId, this.mcps.length);
      return this.mcps;
    } catch (e: unknown) {
      console.error(e);
      throw new Error("Error loading MCPs for bot.");
    }
  }

  public async createMcp(botId: string, mcpConfig: CreateMCPConfig): Promise<MCPConfig> {
    try {
      const result = await firstValueFrom(this.http.post<MCPConfig>(`/api/toolbox/bot/admin/mcp/create/${botId}`, mcpConfig));
      this.mcps.push(result);
      this.counter.setMcps(botId, this.mcps.length);
      return result;
    } catch (e: unknown) {
      console.error(e);
      throw new Error("Error creating MCP for bot.");
    }
  }

  public async updateMcp(botId: string, mcpId: string, mcpConfig: UpdateMCPConfig): Promise<MCPConfig> {
    try {
      const result = await firstValueFrom(this.http.put<MCPConfig>(`/api/toolbox/bot/admin/mcp/update/${botId}/${mcpId}`, mcpConfig));
      this.mcps = this.mcps.map(mcp => {
        if (mcp._id === result._id) {
          return result;
        }
        return mcp;
      });
      this.counter.setMcps(botId, this.mcps.length);
      return result;
    } catch (e: unknown) {
      console.error(e);
      throw new Error("Error updating MCP for bot.");
    }
  }

  public async deleteMcp(botId: string, mcpId: string): Promise<MCPConfig> {
    try {
      const result = await firstValueFrom(this.http.delete<MCPConfig>(`/api/toolbox/bot/admin/mcp/delete/${botId}/${mcpId}`));
      this.mcps = this.mcps.filter(mcp => {
        return mcp._id != result._id;
      });
      this.counter.setMcps(botId, this.mcps.length);
      return result;
    } catch (e: unknown) {
      console.error(e);
      throw new Error("Error deleting MCP for bot.");
    }
  }

  public async loadManagesMcps(): Promise<ManagedMCPServer[]> {
    try {
      const result = await firstValueFrom(this.http.get<ManagedMCPServer[]>(`/api/toolbox/bot/admin/mcp/getmanagedmcps`));
      this.managedServers = result;
      return this.managedServers;
    } catch (e: unknown) {
      console.error(e);
      throw new Error("Error loading MCP from remote Server.");
    }
  }

  public async loadMcpTools(botId: string, server_url: string, authorization?: string, headers?: Record<string, string>): Promise<MCPTool[]> {
    try {
      return await firstValueFrom(this.http.post<MCPTool[]>(`/api/toolbox/bot/admin/mcp/getmcptools/${botId}`, { server_url, authorization, headers }));
    } catch (e: unknown) {
      console.error(e);
      throw new Error("Error loading MCP from remote Server.");
    }
  }
}
